<template>
    <div class="login-page" :class="[backgroundTypeClass, { 'is-loading': loading }]" :style="backgroundStyle">
        <div class="login-wrapper" v-if="!loading">
            <div class="login-header" v-if="showPlaceLogo || placeName">
                <img v-if="showPlaceLogo" :src="logoUrl" alt="Logo" class="place-logo" />
                <div v-if="backgroundTypeClass === 'bg-type-solid' && placeName" class="place-badge default-flow" :style="badgeStyle">
                    {{ placeName }}
                </div>
            </div>

            <div class="login-card-container">
                <div class="place-badge absolute-top" :style="badgeStyle" v-if="backgroundTypeClass === 'bg-type-image' && placeName">
                    {{ placeName }}
                </div>

                <div class="login-card">
                    <slot></slot>
                </div>
            </div>
        </div>

        <div class="login-footer" v-if="!loading">
            <img src="/images/logo-kevi-white.svg" alt="Kevi" class="kevi-logo" @error="hideImage" />
        </div>
    </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { contrastColor, withHash } from '../../helpers/color';

export default {
    name: 'AuthLayout',
    computed: {
        ...mapState({
            place: (state) => state.place
        }),
        ...mapGetters(['globalStatus']),
        loading() {
            return this.globalStatus('loading');
        },
        benefitConfig() {
            return this.place.benefit_program || {};
        },
        loginConfig() {
            return this.benefitConfig.login || { background: { type: 'solid', color: '#0D121C' } };
        },
        backgroundTypeClass() {
            const bg = this.loginConfig.background;
            if (bg && bg.image && bg.image.url) {
                return 'bg-type-image';
            }
            return 'bg-type-solid';
        },
        primaryColor() {
            return withHash(this.benefitConfig.primaryColor || this.place.mainColor || '#2aaa4c');
        },
        backgroundStyle() {
            const bg = this.loginConfig.background || {};
            const solidBg = bg.color || '#0D121C';
            let style = {
                '--solid-bg-color': solidBg,
                '--solid-text-color': contrastColor(solidBg),
                '--primary-color': this.primaryColor
            };

            if (bg && bg.type === 'image' && bg.image && bg.image.url) {
                style.backgroundImage = `url(${bg.image.url})`;
            }
            return style;
        },
        placeName() {
            return this.benefitConfig.name || this.place.name;
        },
        badgeStyle() {
            return {
                backgroundColor: this.primaryColor,
                color: contrastColor(this.primaryColor)
            };
        },
        showPlaceLogo() {
            return !!(this.benefitConfig.logo || this.place.logo);
        },
        logoUrl() {
            let url = this.benefitConfig.logo || this.place.logo;
            if (!url) return '';
            if (url.startsWith('http://') || url.startsWith('https://')) {
                return url;
            }
            return (window.config ? window.config.api_url : 'https://app.kevi.io/') + (url.startsWith('/') ? url.slice(1) : url);
        }
    },
    methods: {
        hideImage(e) {
            e.target.style.display = 'none';
        },
    },
}
</script>

<style lang="scss">
.login-page {
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

@media (max-width: 767px) {
  .login-page {
    background-attachment: scroll;
  }
}

@media (min-width: 768px) {
  .login-page {
    background-attachment: fixed;
  }
}

.login-page.bg-type-solid {
  background-color: var(--solid-bg-color, #0D121C) !important;
}

.login-wrapper {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  padding: 24px 1rem 16px;
  position: relative;
  width: 100%;
}

@media (min-width: 768px) {
  .login-wrapper {
    padding: 8vh 1rem 3rem;
  }
}

.login-header {
  margin-bottom: 24px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  flex-shrink: 0;
}

@media (min-width: 768px) {
  .login-header {
    margin-bottom: 8vh;
  }
}

.bg-type-image .login-header {
  margin-bottom: 0;
}

.login-header .place-logo {
  max-height: 80px;
  max-width: 250px;
  object-fit: contain;
}

.login-card-container {
  width: 100%;
  max-width: 343px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
}

.bg-type-image .login-card-container {
  margin-top: auto;
  margin-bottom: auto;
}

@media (min-width: 768px) {
  .bg-type-image .login-card-container {
    margin-top: auto;
    margin-bottom: auto;
  }
}

.place-badge {
  padding: 8px;
  border-radius: 8px;
  font-weight: 400;
  font-size: 20px;
  line-height: 1.3em;
  text-align: center;
  white-space: nowrap;
  z-index: 10;
}

.place-badge.absolute-top {
  position: absolute;
  top: -16px;
  left: 50%;
  transform: translateX(-50%);
  box-shadow: 0px 15px 24px rgba(0, 0, 0, 0.15);
}

.place-badge.default-flow {
  position: relative;
  box-shadow: none;
}

.login-card {
  background: transparent;
  border-radius: 24px;
  padding: 32px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  position: relative;
  z-index: 1;
}

.bg-type-image .login-card {
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(24.85px);
  -webkit-backdrop-filter: blur(24.85px);
  box-shadow: 0px 27px 31px 0px rgba(0, 0, 0, 0.25);
}

.login-footer {
  background-color: #0D121C;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 60px;
  flex: 0 0 auto;
  width: 100%;
  overflow: hidden;
  padding: 0 !important;
  margin: 0 !important;

  @media (min-width: 1024px) {
    height: 72px;
    flex: 0 0 72px;
  }
}

.kevi-logo {
  height: 25px;
  display: block;

  @media (min-width: 1024px) {
    height: 30px;
  }
}

.login-subtitle {
  text-align: center;
  color: var(--solid-text-color, #FFFFFF);
  font-weight: 500;
  font-size: 16px;
  line-height: 1.3em;
  margin: 0;
}

.bg-type-image .login-subtitle {
  color: #FFFFFF;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.input-group label {
  font-size: 16px;
  font-weight: 500;
  color: var(--solid-text-color, #FCFCFD);
  display: flex;
  gap: 4px;
  line-height: 1.3em;
}

.bg-type-image .input-group label {
  color: #FCFCFD;
}

.input-group .required-asterisk {
  color: #F04438;
  font-size: 18px;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  background: #F9FAFB;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  height: 44px;
  overflow: hidden;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus-within {
    border-color: var(--primary-color, #3182CE);
    box-shadow: 0 0 0 1px var(--primary-color, #3182CE);
  }
}

.has-error .input-wrapper {
  border-color: #F04438;
}

.input-wrapper .input-icon {
  padding-left: 12px;
  padding-right: 12px;
  color: #384250;
  display: flex;
  align-items: center;
  justify-content: center;
}

.input-wrapper input {
  flex-grow: 1;
  padding: 12px;
  border: none;
  outline: none;
  font-size: 14px;
  color: #0D121C;
  background: transparent;
  width: 100%;

  &::placeholder {
    color: #9DA4AE;
  }
}

.input-wrapper .input-icon + input {
  padding-left: 0;
}

.error-message {
  font-size: 12px;
  color: #FF8A8A;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  margin-top: 4px;
}

.btn-primary {
  background: #FCFCFD;
  color: #1F2A37;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  padding: 12px;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.2em;
  cursor: pointer;
  transition: transform 0.1s, opacity 0.2s;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 44px;

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
}

.btn-primary.is-loading {
  color: transparent;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    width: 20px;
    height: 20px;
    border: 2px solid #1F2A37;
    border-top-color: transparent;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
}

.action-buttons-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.action-buttons-row .btn-primary {
  flex-grow: 1;
}

.login-resend {
  background: none;
  border: none;
  color: var(--solid-text-color, rgba(255, 255, 255, 0.9));
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  margin-top: 4px;
  padding: 10px 8px;
  text-align: center;
  width: 100%;
}

.login-resend:disabled {
  opacity: 0.6;
  cursor: default;
}

.login-resend:not(:disabled):hover {
  color: var(--solid-text-color, #FFFFFF);
  text-decoration: underline;
}

.bg-type-image .login-resend {
  color: rgba(255, 255, 255, 0.9);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.bg-type-image .login-resend:disabled {
  color: rgba(255, 255, 255, 0.55);
  opacity: 1;
}

.bg-type-image .login-resend:not(:disabled):hover {
  color: #FFFFFF;
}

.btn-icon {
  background: #FCFCFD;
  color: #1F2A37;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.1s, opacity 0.2s;
  flex: 0 0 auto;

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
